import { useNavigate } from "react-router-dom";
import { Accordion, AccordionSummary, AccordionDetails, Box, Chip, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import dayjs from "dayjs";
import { getProgressStatusStyle } from "../../theme/statusColors";
import { C, compactCardSx } from "../../theme/dashboardCardTokens";
import { useTranslation } from "../../i18n/LanguageContext";
import StatusTimeline from "./StatusTimeline";
import StatusTimelineNotes from "./StatusTimelineNotes";
import OrderTestActionChips from "./OrderTestActionChips";
import CancelledTestBadge from "./CancelledTestBadge";

// Steps from "returned to lab" onward - the point a test has an analysis to open.
const ANALYSIS_STEP_KEYS = ["returned_to_lab", "processing", "analysis_completed", "completed"];

// Collapsed-header summary: one chip per distinct status among this order's
// tests (e.g. "2 Cancelled"), not one dot per test — so two cancelled tests
// read as a single grouped chip instead of two same-colored dots that don't
// say what they mean.
function summarizeTestStatuses(tests) {
  const counts = new Map();
  tests.forEach((test) => {
    const key = test.current_step_key;
    counts.set(key, (counts.get(key) || 0) + 1);
  });
  return Array.from(counts.entries()).map(([key, count]) => ({ key, count }));
}

// Expandable order card shared by the patient's My Orders page and the
// therapist's per-patient orders page — same component, different data
// source, response shape is identical between the two per the backend doc.
// `role` controls whether the "Fill out your form" chip can appear;
// `getReportLink` is the per-role report-link fetcher (patient vs therapist).
// `defaultExpanded` is driven by the parent list (only the first/top card
// opens by default — every other card starts collapsed).
function OrderCard({ order, role, getReportLink, defaultExpanded = false }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <Accordion
      disableGutters
      square={false}
      defaultExpanded={defaultExpanded}
      sx={{
        ...compactCardSx,
        mb: "16px",
        "&:before": { display: "none" },
        overflow: "hidden",
        // MUI's Accordion only rounds the outer corners of the first/last
        // item in a stacked group (square=false default), leaving every
        // card's OTHER two corners at borderRadius: 0 — that's the "first
        // card square on top, last card square on bottom" asymmetry. Force
        // every corner on every card back to the same radius.
        "&:first-of-type, &:last-of-type": { borderRadius: "12px" },
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", pr: 1 }}>
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: "0.9375rem" }}>{order.order_name}</Typography>
            <Typography sx={{ fontSize: "0.75rem", color: "#6b7280" }}>
              {order.processed_at ? dayjs(order.processed_at).format("MMM D, YYYY") : "—"}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: { xs: "nowrap", sm: "wrap" } }}>
            {summarizeTestStatuses(order.tests).map(({ key, count }, i) => {
              const style = getProgressStatusStyle(key);
              const label = t(`portal.statusTimeline.steps.${key}.label`) || key;
              return (
                <Chip
                  key={key}
                  size="small"
                  label={`${count} ${label}`}
                  sx={{
                    ...style,
                    fontSize: "0.6875rem",
                    fontWeight: 600,
                    height: "22px",
                    "& .MuiChip-label": { px: 1 },
                    // Mobile: only the first status chip is shown inline; any
                    // others collapse into the "+N" badge below instead of
                    // wrapping/crowding the card header.
                    display: i === 0 ? "inline-flex" : { xs: "none", sm: "inline-flex" },
                  }}
                />
              );
            })}
            {summarizeTestStatuses(order.tests).length > 1 && (
              <Box
                sx={{
                  display: { xs: "flex", sm: "none" },
                  alignItems: "center",
                  justifyContent: "center",
                  width: 20,
                  height: 20,
                  borderRadius: "50%",
                  bgcolor: "#e5e7eb",
                  color: "#374151",
                  fontSize: "0.625rem",
                  fontWeight: 700,
                  flexShrink: 0,
                }}
              >
                +{summarizeTestStatuses(order.tests).length - 1}
              </Box>
            )}
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        {order.tests.map((test, idx) => (
          <Box
            key={test.lab_test_id}
            sx={{
              pt: idx === 0 ? 0 : 2,
              mt: idx === 0 ? 0 : 2,
              borderTop: idx === 0 ? "none" : "1px solid #f0f0f0",
            }}
          >
            <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600, mb: 0.5 }}>
              {test.substrate}{" "}
              <Typography component="span" sx={{ color: "#9ca3af", fontWeight: 400 }}>
                ({test.test_identifier})
              </Typography>
            </Typography>

            {test.current_step_key === "cancelled" ? (
              <CancelledTestBadge test={test} />
            ) : (
              <>
                <StatusTimeline timeline={test.timeline} rawLabels={role === "admin"} />
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    alignItems: { xs: "stretch", sm: "flex-end" },
                    justifyContent: "space-between",
                    gap: { xs: 1, sm: 2 },
                    mt: 0.5,
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <StatusTimelineNotes timeline={test.timeline} />
                  </Box>
                  <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                    {order.order_number != null && ANALYSIS_STEP_KEYS.includes(test.current_step_key) && (
                      <Chip
                        clickable
                        size="small"
                        onClick={() => navigate(`/analysis/${order.order_number}/${test.test_identifier}`)}
                        label={
                          <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                            Open Analysis
                            <ArrowOutwardIcon sx={{ fontSize: 14 }} />
                          </Box>
                        }
                        sx={{
                          bgcolor: "#FFFFFF",
                          color: C.primary,
                          fontWeight: 600,
                          borderRadius: "8px",
                          border: `1px solid ${C.primary}40`,
                          pl: 0.5,
                          pr: 0.5,
                          "&:hover": { bgcolor: C.tintBg },
                        }}
                      />
                    )}
                    <OrderTestActionChips
                      test={test}
                      showFormChip={role === "patient" || role === "admin"}
                      formChipVariant={role === "admin" ? "copyLink" : "fill"}
                      getReportLink={getReportLink}
                    />
                  </Box>
                </Box>
              </>
            )}
          </Box>
        ))}
      </AccordionDetails>
    </Accordion>
  );
}

export default OrderCard;
